import { selectedProcessIdAtom } from '../../atoms/workspace';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { trpc } from '../../lib/trpc';
import { type ProcessDoc } from '../../rxdb/collections/processes';
import { useDatabase } from '../../rxdb/provider';
import { DatePicker } from '../DatePicker';
import {
  ActionIcon,
  Button,
  Menu,
  Popover,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs, { type ManipulateType } from 'dayjs';
import { useSetAtom } from 'jotai';
import {
  Archive,
  ArchiveRestore,
  ArrowRight,
  Pin,
  PinOff,
  Timer,
  TimerOff,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';

const snoozeShortcuts: Array<{
  label: string;
  value: [number, ManipulateType];
}> = [
  { label: '1d', value: [1, 'day'] },
  { label: '3d', value: [3, 'day'] },
  { label: '1w', value: [1, 'week'] },
  { label: '2w', value: [2, 'week'] },
  { label: '1m', value: [1, 'month'] },
  { label: '3m', value: [3, 'month'] },
  { label: '6m', value: [6, 'month'] },
  { label: '1y', value: [1, 'year'] },
];

type ProcessDetailActionsProps = {
  readonly archivedAt: Date | null;
  readonly isPinned: boolean;
  readonly processId: string;
  readonly snoozedUntil: Date | null;
  readonly workspaceId: string;
};

export const ProcessDetailActions = ({
  archivedAt,
  isPinned,
  processId,
  snoozedUntil,
  workspaceId,
}: ProcessDetailActionsProps) => {
  const [showSnoozeCalendar, setShowSnoozeCalendar] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const theme = useMantineTheme();
  const utils = trpc.useUtils();
  const { db } = useDatabase();
  const setSelectedProcessId = useSetAtom(selectedProcessIdAtom);

  const { workspaces } = useWorkspaces();
  const otherWorkspaces = workspaces.filter((ws) => ws.id !== workspaceId);

  const { mutateAsync: updateProcess } = trpc.processes.update.useMutation({
    onSuccess: () => {
      utils.processes.listArchived.invalidate();
    },
  });

  const { mutateAsync: destroyProcess } = trpc.processes.destroy.useMutation({
    onSuccess: () => utils.processes.listArchived.invalidate(),
  });

  const patchLocalProcess = async (patch: Partial<ProcessDoc>) => {
    const doc = await db.processes.findOne(processId).exec();
    await doc?.patch({ ...patch, updatedAt: new Date().toISOString() });
  };

  const handlePinToggle = async () => {
    const nextIsPinned = !isPinned;
    const result = await updateProcess({ isPinned: nextIsPinned, processId });
    if (result.ok) {
      await patchLocalProcess({ isPinned: nextIsPinned });
    }
  };

  const handleSnooze = async (date: Date) => {
    const result = await updateProcess({ processId, snoozedUntil: date });
    if (result.ok) {
      await patchLocalProcess({ snoozedUntil: date.toISOString() });
    }

    setShowSnoozeCalendar(false);
    setSelectedProcessId(null);
  };

  const handleRemoveSnooze = async () => {
    const result = await updateProcess({ processId, snoozedUntil: null });
    if (result.ok) {
      await patchLocalProcess({ snoozedUntil: null });
    }
  };

  const handleArchive = async () => {
    const nextArchivedAt = new Date();
    const result = await updateProcess({
      archivedAt: nextArchivedAt,
      processId,
    });
    if (result.ok) {
      await patchLocalProcess({ archivedAt: nextArchivedAt.toISOString() });
    }

    notifications.show({ message: 'Process archived' });
    setSelectedProcessId(null);
  };

  const handleUnarchive = async () => {
    const result = await updateProcess({ archivedAt: null, processId });
    if (result.ok) {
      await patchLocalProcess({ archivedAt: null });
    }

    notifications.show({ message: 'Process restored' });
    setSelectedProcessId(null);
  };

  const handleMoveTo = async (targetWorkspaceId: string) => {
    const result = await updateProcess({
      processId,
      workspaceId: targetWorkspaceId,
    });
    if (result.ok) {
      await patchLocalProcess({ workspaceId: targetWorkspaceId });
    }

    notifications.show({ message: 'Process moved' });
    setSelectedProcessId(null);
  };

  const handleDelete = async () => {
    const result = await destroyProcess({ processId });
    if (result.ok) {
      const doc = await db.processes.findOne(processId).exec();
      await doc?.remove();
    }

    notifications.show({ message: 'Process deleted' });
    setSelectedProcessId(null);
  };

  const isSnoozed = snoozedUntil && new Date(snoozedUntil) > new Date();

  return (
    <div className="flex items-center gap-1">
      <Tooltip label={isPinned ? 'Unpin' : 'Pin'}>
        <ActionIcon
          aria-label={isPinned ? 'Unpin' : 'Pin'}
          color="gray"
          onClick={handlePinToggle}
          variant="subtle"
        >
          {isPinned ? <PinOff size={16} /> : <Pin size={16} />}
        </ActionIcon>
      </Tooltip>

      <Popover
        onChange={setShowSnoozeCalendar}
        opened={showSnoozeCalendar}
        position="bottom-end"
        shadow="md"
        withArrow
      >
        <Popover.Target>
          <Tooltip label={isSnoozed ? 'Snoozed — click to change' : 'Snooze'}>
            <ActionIcon
              aria-label={isSnoozed ? 'Snoozed — click to change' : 'Snooze'}
              color={isSnoozed ? 'orange' : 'gray'}
              onClick={() => setShowSnoozeCalendar((prev) => !prev)}
              variant="subtle"
            >
              <Timer size={16} />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown>
          <div className="flex flex-wrap gap-1 mb-2">
            {snoozeShortcuts.map((shortcut) => (
              <Button
                key={shortcut.label}
                onClick={() =>
                  handleSnooze(
                    dayjs()
                      .add(...shortcut.value)
                      .toDate(),
                  )
                }
                size="compact-xs"
                variant="light"
              >
                {shortcut.label}
              </Button>
            ))}
          </div>
          <DatePicker
            excludeDate={(date_: string) => new Date(date_) < new Date()}
            getDayProps={(date_: string) =>
              dayjs(date_).isSame(dayjs(), 'day')
                ? {
                    style: {
                      backgroundColor: theme.colors.blue[1],
                      color: theme.colors.gray[6],
                    },
                  }
                : {}
            }
            onChange={(date) => {
              if (date) {
                handleSnooze(new Date(date as unknown as string));
              }
            }}
          />
          {isSnoozed && (
            <Button
              className="mt-2"
              color="orange"
              fullWidth
              leftSection={<TimerOff size={14} />}
              onClick={handleRemoveSnooze}
              size="compact-sm"
              variant="light"
            >
              Remove snooze
            </Button>
          )}
        </Popover.Dropdown>
      </Popover>

      {archivedAt ? (
        <Tooltip label="Unarchive">
          <ActionIcon
            aria-label="Unarchive"
            color="gray"
            onClick={handleUnarchive}
            variant="subtle"
          >
            <ArchiveRestore size={16} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <Tooltip label="Archive">
          <ActionIcon
            aria-label="Archive"
            color="gray"
            onClick={handleArchive}
            variant="subtle"
          >
            <Archive size={16} />
          </ActionIcon>
        </Tooltip>
      )}

      {otherWorkspaces.length > 0 && (
        <Menu
          position="bottom-end"
          shadow="md"
        >
          <Menu.Target>
            <Tooltip label="Move to...">
              <ActionIcon
                aria-label="Move to..."
                color="gray"
                variant="subtle"
              >
                <ArrowRight size={16} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Move to workspace</Menu.Label>
            {otherWorkspaces.map((ws) => (
              <Menu.Item
                key={ws.id}
                onClick={() => handleMoveTo(ws.id)}
              >
                {ws.title}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}

      {showDeleteConfirm ? (
        <div className="flex items-center gap-1 ml-2">
          <Text
            c="dimmed"
            size="xs"
          >
            Delete?
          </Text>
          <Button
            color="red"
            onClick={handleDelete}
            size="compact-xs"
          >
            Yes
          </Button>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            size="compact-xs"
            variant="default"
          >
            No
          </Button>
        </div>
      ) : (
        <Tooltip label="Delete">
          <ActionIcon
            aria-label="Delete"
            className="ml-auto"
            color="red"
            onClick={() => setShowDeleteConfirm(true)}
            variant="subtle"
          >
            <Trash2 size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </div>
  );
};
